import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { combineLatest, map, merge, Subscription, switchMap, tap } from 'rxjs';

import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { AccountManagementService } from '../../services/account-management.service';
import { QuotaPreferenceService } from '../../services/quota-preference.service';
import { MediaSubscriptionPreferenceService } from '../../services/media-subscription-preference.service';

import { MonitoringConfigsModel, SubscriptionLicenses } from '../../models/monitoring.configs.model';
import { BriefingConfig } from '../../models/briefing-config.model';
import { AutomationAgentQuotaModel } from '../../models/automation-agent-quota.model';
import { MentionImportTypes } from '../../../../Common/MonitorShared/misc/mention-import-types';
import { ApiKey } from '../../models/api-key';

import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { MentionImportTypesEnum } from '../../../../Common/MonitorShared/enum/mention-import-types.enum';
import { LicensesEnum } from '../../../../api/monitoring/licenses.enum';
import { MessageService } from 'primeng/api';
import { MonitoringQuotaTypesEnum } from '@api/mentions/enum/monitoring-quota-types.enum';


enum TVEyesPodcastLicenseGroup {
    NONE,
    ALL_PODCASTS,
    FINANCIAL_PODCASTS_ONLY
}

@Component({
    selector: 'agpr-monitoring-preferences',
    templateUrl: './monitoring-preferences.component.html',
    styleUrls: ['./monitoring-preferences.component.scss'],
    providers: [QuotaPreferenceService],
    standalone: false
})
export class MonitoringPreferencesComponent implements OnInit, OnDestroy {

    @Input() hasMonitoring: boolean;

    monitoringConfigs: MonitoringConfigsModel;
    briefingConfigs: BriefingConfig;
    licenses: SubscriptionLicenses = {};
    isReadOnly: boolean = false;
    disableInputs: boolean = false;
    lockSubscriptions: boolean = false;
    hasSubscriptions: boolean = true;
    mentionImportTypes: MentionImportTypes = {};
    enableAiFeatures: boolean;
    hasAutomationAgents: boolean = false;
    automationAgentQuota: AutomationAgentQuotaModel = new AutomationAgentQuotaModel();
    prominenceInUse: number;
    displayFinancialTimesPodcastUpsell: boolean = false;
    podcastLicenseGroupChecked: boolean = false;
    podcastLicenseGroup: TVEyesPodcastLicenseGroup = TVEyesPodcastLicenseGroup.NONE;
    enableAutoSavingTopic: boolean = false;
    autoSavingTopicErrorMessage: string = null;
    hasMediaMonitoringApis: boolean = false;
    hasApiKeyManagers: boolean = false;
    apiKeys: ApiKey[] = [];

    private subscriptions: Subscription[] = [];

    readonly licensesEnum = LicensesEnum;
    readonly mentionImportTypesEnum = MentionImportTypesEnum;
    readonly tvEyesPodcastLicenseGroup = TVEyesPodcastLicenseGroup;
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly maxAutoSavingLimit: number = 25;
    readonly monitoringQuotaTypes = MonitoringQuotaTypesEnum;

    constructor(
        public accountManagementService: AccountManagementService,
        private quotaPreferencesService: QuotaPreferenceService,
        private mediaSubscriptionPreferenceService: MediaSubscriptionPreferenceService,
        private localizationService: LocalizationService,
        private messageService: MessageService
    ) {}

    ngOnInit(): void {
        // We need to listen to the licenses changes to reset the hasSubscriptions boolean
        const licenses$ = merge(
            this.mediaSubscriptionPreferenceService.licenses$,
            this.accountManagementService.licensesChanged$.pipe(
                switchMap(() => this.mediaSubscriptionPreferenceService.licenses$)
            )
        );

        this.subscriptions.push(
            combineLatest([
                this.accountManagementService.getMonitoringConfig(),
                licenses$,
                this.mediaSubscriptionPreferenceService.lockSubscriptions$
            ]).subscribe(([config, licenses, shouldLockSubscriptions]) => {
                this.monitoringConfigs = config;
                this.licenses = licenses;
                this.lockSubscriptions = shouldLockSubscriptions;

                this.hasSubscriptions =
                    this.accountManagementService.accountHasSubscriptionPermissions()
                    || licenses[LicensesEnum.TVEYES]
                    || shouldLockSubscriptions;

                if (this.hasSubscriptions) {
                    this.quotaPreferencesService.setMonitoringSubscriptionsEnabled(true);
                }

                this.setPodcastDisplayGroupFromLicense();
                this.handleLicenseChange();

                this.accountManagementService.toggleMediaSubscriptionPermission(this.hasSubscriptions);
                this.mentionImportTypes = config.mentionImportTypes;
                this.enableAutoSavingTopic = !!this.monitoringConfigs.autoSaveTopicLimit;
            }),
            this.accountManagementService.getBriefingConfig().subscribe((config) => {
                this.briefingConfigs = config;
            }),
            this.accountManagementService.getReadOnly().subscribe(isReadOnly => {
                    this.isReadOnly = isReadOnly;
                    this.disableInputs = isReadOnly;
            }),
            this.accountManagementService.hasOpenAiPermission$.subscribe(x => this.enableAiFeatures = x),
            this.accountManagementService.hasAgenticFeaturePermission$.subscribe(x => this.hasAutomationAgents = x),
            this.accountManagementService.getAutomationAgentQuota().subscribe(quota => this.automationAgentQuota = quota),
            this.accountManagementService.prominenceInUse$.subscribe(x => this.prominenceInUse = x),
            this.accountManagementService.hasMediaMonitoringApis$.subscribe(x => this.hasMediaMonitoringApis = x),
            this.accountManagementService.hasApiKeyManagers$.subscribe(x => this.hasApiKeyManagers = x),
            this.accountManagementService.selectedAccountApiKeys$.subscribe(apiKeys => {
                this.apiKeys = apiKeys;
            })
        );
    }


    handleLicenseChange() {
        this.displayFinancialTimesPodcastUpsell = this.licenses[LicensesEnum.FINANCIAL_TIMES]
            && (!this.licenses[LicensesEnum.PODCASTS] && !this.licenses[LicensesEnum.FINANCIAL_PODCASTS]);
    }

    /**
     * Called when the subscriptions checkbox is selected, autofilling default selections
     */
    toggleAllSubscriptions(enable: boolean) {
        this.quotaPreferencesService.setMonitoringSubscriptionsEnabled(enable);
        this.accountManagementService.toggleMediaSubscriptionPermission(enable);

        this.enableAllMediaSubscriptions(enable);

        if (!enable) {
            // Remove podcasts when unchecking subscriptions, but don't enable when rechecking
            this.licenses[LicensesEnum.PODCASTS] = enable;
            this.licenses[LicensesEnum.FINANCIAL_PODCASTS] = enable;
            this.setPodcastDisplayGroupFromLicense();
            this.handleLicenseChange();
        }
    }

    handleMentionInsightChange() {
        if (!this.monitoringConfigs.hasMentionInsights) {
            this.briefingConfigs.insightAutoGenerationBriefings = false;
        }
    }

    handlePodcastLicenseGroupChanged() {
        if (this.podcastLicenseGroupChecked) {
            this.podcastLicenseGroup = TVEyesPodcastLicenseGroup.ALL_PODCASTS;
        }
        this.calculatePodcastLicenseFromPodcastDisplayGroup();
        this.handleLicenseChange();
    }

    setPodcastDisplayGroupFromLicense() {
        if (this.licenses[LicensesEnum.PODCASTS]) {
            this.podcastLicenseGroupChecked = true;
            this.podcastLicenseGroup = TVEyesPodcastLicenseGroup.ALL_PODCASTS;
        } else if (this.licenses[LicensesEnum.FINANCIAL_PODCASTS]) {
            this.podcastLicenseGroupChecked = true;
            this.podcastLicenseGroup = TVEyesPodcastLicenseGroup.FINANCIAL_PODCASTS_ONLY;
        } else {
            this.podcastLicenseGroupChecked = false;
            this.podcastLicenseGroup = TVEyesPodcastLicenseGroup.NONE;
        }
    }

    calculatePodcastLicenseFromPodcastDisplayGroup() {
        if (!this.podcastLicenseGroupChecked) {
            this.licenses[LicensesEnum.PODCASTS] = false;
            this.licenses[LicensesEnum.FINANCIAL_PODCASTS] = false;
        } else if (this.podcastLicenseGroup === TVEyesPodcastLicenseGroup.FINANCIAL_PODCASTS_ONLY) {
            this.licenses[LicensesEnum.PODCASTS] = false;
            this.licenses[LicensesEnum.FINANCIAL_PODCASTS] = true;
        } else {
            this.licenses[LicensesEnum.PODCASTS] = true;
            this.licenses[LicensesEnum.FINANCIAL_PODCASTS] = false;
        }
    }

    private enableAllMediaSubscriptions(enable: boolean) {
        this.licenses[LicensesEnum.FACEBOOK] = enable;
        this.licenses[LicensesEnum.INSTAGRAM] = enable;
        this.licenses[LicensesEnum.REDDIT] = enable;
        this.licenses[LicensesEnum.TWITTER] = enable;
        this.licenses[LicensesEnum.YOUTUBE] = enable;
    }

    toggleAutoSavingTopic() {
        this.autoSavingTopicErrorMessage = null;
        if (!this.enableAutoSavingTopic) {
            this.monitoringConfigs.autoSaveTopicLimit = null;
        } else {
            this.monitoringConfigs.autoSaveTopicLimit = 5;
        }
    }

    checkAutoSaveLimit() {
        this.autoSavingTopicErrorMessage = null;
        if (this.monitoringConfigs.autoSaveTopicLimit > this.maxAutoSavingLimit) {
            this.autoSavingTopicErrorMessage = this.localizationService.transform(
                'AMT.monitoring.autoSaving.limit.error.exceededLimit', {max: this.maxAutoSavingLimit}
            );
        }
        // cannot set lower than active auto-save topics
        if (this.monitoringConfigs.autoSaveTopicLimit < this.monitoringConfigs?.activeAutoSaveTopicTotal) {
            this.monitoringConfigs.autoSaveTopicLimit = this.monitoringConfigs.activeAutoSaveTopicTotal;
            this.autoSavingTopicErrorMessage = this.localizationService.transform(
                'AMT.monitoring.autoSaving.limit.error.lowerThanActive', {count: this.monitoringConfigs.activeAutoSaveTopicTotal}
            );
        }
    }

    /** Keeps Remaining in step with the limit while it is being edited, as the mention quota does */
    updateAutomationAgentQuotaRemaining(): void {
        this.automationAgentQuota.updateRemaining();
    }

    updateSavingQuotaRemaining() {
        const {
            savingQuota,
            additionalMentionQuota,
            savingQuotaUsed
        } = this.monitoringConfigs;

        if (savingQuota === -1) {
            // -1 means unlimited
            this.monitoringConfigs.savingQuotaRemaining = -1;
        } else {
            this.monitoringConfigs.savingQuotaRemaining = (savingQuota + additionalMentionQuota) - savingQuotaUsed;
        }
    }

    updateApiKeyStatus(apiKey: ApiKey): void {
        const clone = apiKey.clone();
        clone.disabled = !apiKey.disabled;
        this.accountManagementService.updateApiKeyStatusForAccount(clone).subscribe({
            next: () => {
                const idx = this.apiKeys.findIndex(k => k.id === clone.id);
                if (idx > -1) {
                    this.apiKeys.splice(idx, 1, clone);
                }

                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform('AMT.apiKeys.management.update.growlMsg.success.heading', { name: clone.name }),
                    detail: this.localizationService.transform('AMT.apiKeys.management.update.growlMsg.success.message', { name: clone.name })
                });
            },
            error: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform('AMT.apiKeys.management.update.growlMsg.error.heading', { name: clone.name }),
                    detail: this.localizationService.transform('AMT.apiKeys.management.update.growlMsg.error.message', { name: clone.name })
                });
            }
        });
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
